import { Controller } from "@hotwired/stimulus"

// The export template editor's file naming section (TASK-122).
//
// Two jobs: insert a token at the cursor when a chip is clicked, and keep a
// live preview of the resolved name in step with what is typed. The preview
// is resolved server-side — the sanitising and blank-collapse rules are
// fiddly enough that a second implementation here would drift from the one
// that actually names the files.
export default class extends Controller {
  static targets = ["input", "preview"]
  static values = { url: String, versionId: String }

  connect() {
    this.timers = {}
    this.inputTargets.forEach(input => this.refresh(input.dataset.scope))
  }

  disconnect() {
    Object.values(this.timers).forEach(timer => clearTimeout(timer))
  }

  insert(event) {
    event.preventDefault()

    const { token, scope } = event.currentTarget.dataset
    const input = this.inputFor(scope)
    if (!input) return

    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    input.value = `${input.value.slice(0, start)}${token}${input.value.slice(end)}`

    const caret = start + token.length
    input.focus()
    input.setSelectionRange(caret, caret)

    this.refresh(scope)
  }

  changed(event) {
    this.refreshDebounced(event.currentTarget.dataset.scope)
  }

  // --- Private ---

  inputFor(scope) {
    return this.inputTargets.find(input => input.dataset.scope === scope)
  }

  previewFor(scope) {
    return this.previewTargets.find(el => el.dataset.scope === scope)
  }

  refreshDebounced(scope) {
    clearTimeout(this.timers[scope])
    this.timers[scope] = setTimeout(() => this.refresh(scope), 300)
  }

  async refresh(scope) {
    const input = this.inputFor(scope)
    const preview = this.previewFor(scope)
    if (!input || !preview || !this.hasUrlValue) return

    const params = new URLSearchParams({
      pattern: input.value,
      scope: scope,
      form_definition_version_id: this.versionIdValue
    })

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      preview.textContent = data.preview
    } catch {
      // A preview is a convenience, never a blocker — leave the last good one.
    }
  }
}
